import { useEffect, useState } from "react";
import { Box, Text, Spinner, HStack } from "@chakra-ui/react";
import { useFormik, validateYupSchema, yupToFormErrors } from "formik";
import { useTranslation } from "react-i18next";
import { toaster } from "@/components/ui/toaster";
import { stepCircleProps, stepAlertBorderColor, stepLabelColor } from "@/components/jobOffer/ui";
import { createEmptyJobOffer, jobOfferValidationSchema, stepHasErrors } from "@/models/jobOffer";
import { collectScoringValidationIssues, getLocalScoringCriteria } from "@/services/scoringValidation";
import { sumMaxScore, ScoringValidationContext } from "@/scoringUi";
import { ScoringActiveContext, useAccountScoring } from "@/auth/ScoringContext";
import ScoringToggle from "./ScoringToggle";
import { ModelService } from "@/services/ModelService";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import Step1Offer from "./steps/Step1Offer";
import Step2Employment from "./steps/Step2Employment";
import Step3Safety from "./steps/Step3Safety";
import Step4Requirements from "./steps/Step4Requirements";
import Step5AIConfig from "./steps/Step5AIConfig";
import testJobOfferValues from "@/mocks/testJobOfferValues";

// Closable and deduped by id so repeated clicks don't stack toasts over the footer buttons.
const showScoringToast = (type, title) =>
    toaster.create({ id: `scoring-${title}`, type, title, closable: true, duration: 8000 });

const STEP_DEFS = [
    { id: 1, sectionCount: 2 },
    { id: 2, sectionCount: 4 },
    { id: 3, sectionCount: 2 },
    { id: 4, sectionCount: 3 },
    { id: 5, sectionCount: 7 },
];

const useSteps = (t) =>
    STEP_DEFS.map((def) => ({
        id: def.id,
        title: t(`modal.steps.${def.id}.title`),
        shortTitle: t(`modal.steps.${def.id}.shortTitle`),
        sections: Array.from({ length: def.sectionCount }, (_, i) => t(`modal.steps.${def.id}.sections.${i}`)),
    }));

const JobOfferFormModal = ({ onClose, initialValues, onSubmit, agentName }) => {
    const { t } = useTranslation("jobOffer");
    const STEPS = useSteps(t);
    const [currentStep, setCurrentStep] = useState(1);
    const [saved, setSaved] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [jsonInput, setJsonInput] = useState("");
    const [jsonError, setJsonError] = useState("");
    const [isDraft, setIsDraft] = useState(false);
    const [visitedSteps, setVisitedSteps] = useState(() => new Set());
    const [warningErrors, setWarningErrors] = useState({});
    // Lifted out of Step5AIConfig so the sidebar's points summary can read it too, not just
    // the step content -- see InheritedCriteriaSection's onModelLoaded.
    const [scoringModel, setScoringModel] = useState(null);
    const [showScoringErrors, setShowScoringErrors] = useState(false);
    const [scoringErrorSteps, setScoringErrorSteps] = useState(() => new Set());
    const totalSteps = STEPS.length;

    const formik = useFormik({
        initialValues: initialValues ?? createEmptyJobOffer(),
        validationSchema: jobOfferValidationSchema,
        // Silent until the first failed publish attempt (no red before the user asks to publish),
        // then live — so fixing a field or removing an invalid item clears its red state right away
        // instead of staying stuck until the next publish attempt.
        validateOnChange: submitAttempted,
        validateOnBlur: submitAttempted,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await onSubmit?.(values, false);
            } catch (error) {
                toaster.create({
                    type: "error",
                    title: extractBusinessErrorMessage(error) ?? t("submitError", "Failed to save the job offer"),
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Account switch AND offer switch.
    const { accountScoringEnabled } = useAccountScoring();
    const scoringActive = accountScoringEnabled && !!formik.values.scoringEnabled;

    const goToStep = (stepId) => {
        if (stepId === currentStep) return;
        setVisitedSteps((prev) => new Set(prev).add(currentStep));
        validateYupSchema(formik.values, jobOfferValidationSchema)
            .then(() => setWarningErrors({}))
            .catch((err) => setWarningErrors(yupToFormErrors(err)));
        setCurrentStep(stepId);
    };

    // Keeps the "left incomplete" (yellow) warnings in sync while the user keeps editing without
    // navigating away and back — otherwise a field fixed in place stays flagged as a stale warning
    // until the next step change. Only runs once a step has actually been left once, matching the
    // existing "warn on fields left incomplete" behavior; debounced so it doesn't re-validate the
    // whole schema on every keystroke.
    useEffect(() => {
        if (visitedSteps.size === 0) return undefined;
        const handle = setTimeout(() => {
            validateYupSchema(formik.values, jobOfferValidationSchema)
                .then(() => setWarningErrors({}))
                .catch((err) => setWarningErrors(yupToFormErrors(err)));
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values, visitedSteps.size]);

    const handleSaveJson = () => {
        const blob = new Blob([JSON.stringify(formik.values, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `job-offer-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleLoadJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            formik.setValues(parsed);
            setShowJsonModal(false);
            setJsonInput("");
            setJsonError("");
        } catch {
            setJsonError("Nieprawidłowy format JSON");
        }
    };

    const handleLoadMockup = () => {
        formik.setValues(testJobOfferValues);
        setShowJsonModal(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setJsonInput(ev.target.result);
        reader.readAsText(file);
    };

    const handleShowSavedIndicator = () => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    // Flags invalid scoring in the form, jumps to the first affected step and scrolls to the first red panel.
    const revealScoringIssues = (issues) => {
        const steps = issues.map((i) => i.step).filter(Boolean);
        setScoringErrorSteps(new Set(steps));
        setShowScoringErrors(true);
        if (steps.length) setCurrentStep(Math.min(...steps));
        setTimeout(() => document.querySelector('[data-scoring-invalid="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    };

    const handleSaveDraft = async () => {
        setIsDraft(true);
        formik.setSubmitting(true);
        try {
            const payload = { ...formik.values, basicInfo: { ...formik.values.basicInfo, offerStatus: "draft" } };
            await onSubmit?.(payload, true);
            handleShowSavedIndicator();
            // Non-blocking, unlike handlePublish's use of the same check: a draft must still save
            // even with an incomplete scoring rule (it's stored as 0 pkt / "" until finished -- see
            // apiMapping.js), but the user should be told what's incomplete rather than silently
            // guessing later why a criterion scores 0. Skips the locked-edit/duplicate-key checks
            // (they need the scenario's scoring model, not worth an extra request on every draft
            // save) -- those still run for real at publish time.
            const draftIssues = collectScoringValidationIssues(formik.values);
            draftIssues.forEach(({ text }) => showScoringToast("warning", text));
            if (draftIssues.length) revealScoringIssues(draftIssues);
        } catch (error) {
            toaster.create({
                type: "error",
                title: extractBusinessErrorMessage(error) ?? t("submitError", "Failed to save the job offer"),
            });
        } finally {
            formik.setSubmitting(false);
            setIsDraft(false);
        }
    };

    const handlePublish = async () => {
        setSubmitAttempted(true);
        const errors = await formik.validateForm();
        const firstInvalidStep = STEPS.find((s) => stepHasErrors(s.id, errors));
        if (firstInvalidStep) {
            setCurrentStep(firstInvalidStep.id);
            return;
        }

        const values = formik.values;
        if (scoringActive) {
            let modelCriteria;
            try {
                const model = values.id
                    ? await ModelService.getRecruitmentScoringModel(values.id)
                    : values.scenarioId
                        ? await ModelService.getScenarioScoringModel(values.scenarioId)
                        : null;
                modelCriteria = model?.criteria;
            } catch {
                modelCriteria = undefined; // scenario model unavailable -- still run the checks that don't need it
            }
            const scoringIssues = collectScoringValidationIssues(values, modelCriteria);
            if (scoringIssues.length) {
                scoringIssues.forEach(({ text }) => showScoringToast("error", text));
                revealScoringIssues(scoringIssues);
                return;
            }
        }

        setIsDraft(false);
        formik.handleSubmit();
    };

    const activeStep = STEPS[currentStep - 1];
    const activeStepHasErrors = submitAttempted && stepHasErrors(currentStep, formik.errors);
    const stepNeedsForceOpen = (stepId) =>
        (submitAttempted && stepHasErrors(stepId, formik.errors)) ||
        (visitedSteps.has(stepId) && stepHasErrors(stepId, warningErrors)) ||
        (showScoringErrors && scoringErrorSteps.has(stepId));

    // Live (form-state) recruitment side + fetched-once scenario side, matching how
    // InheritedCriteriaSection/jobOfferMapper.js split the two sources.
    const localCriteria = getLocalScoringCriteria(formik.values);
    const recruitmentMaxScore = sumMaxScore(localCriteria);
    const recruitmentCriteriaCount = localCriteria.filter((c) => c.scoring?.scoringEnabled).length;
    const scenarioMaxScore = scoringModel?.scenarioMaxScore ?? 0;
    const scenarioCriteriaCount = (scoringModel?.criteria ?? []).filter((c) => c.source === "Scenario" && c.scoring?.scoringEnabled).length;
    const totalMaxScore = recruitmentMaxScore + scenarioMaxScore;

    const renderDraftButton = (size = "sm") => (
        <Box
            as="button"
            type="button"
            onClick={handleSaveDraft}
            disabled={formik.isSubmitting}
            px={size === "xs" ? 3 : 4}
            py={size === "xs" ? 1.5 : 2}
            borderRadius={8}
            fontSize={size === "xs" ? "xs" : "xs"}
            bg="white"
            color="gray.700"
            border="1px solid"
            borderColor="gray.300"
            opacity={formik.isSubmitting ? 0.5 : 1}
            cursor={formik.isSubmitting ? "not-allowed" : "pointer"}
        >
            {formik.isSubmitting && isDraft ? <Spinner size="xs" /> : t("modal.saveDraft")}
        </Box>
    );

    return (
        <>
        <Box
            position="fixed"
            inset={0}
            zIndex={50}
            display="flex"
            alignItems="stretch"
            bg="rgba(0,0,0,0.85)"
        >
            <Box
                position="relative"
                display="flex"
                w="full"
                m={{ base: 0, md: 4 }}
                borderRadius={{ base: 0, md: 16 }}
                overflow="hidden"
                bg="#F6F6FB"
                maxH={{ base: '100vh', md: 'calc(100vh - 2rem)' }}
            >
                {/* Desktop sidebar */}
                <Box
                    flexShrink={0}
                    display={{ base: 'none', md: 'flex' }}
                    flexDirection="column"
                    py={6}
                    overflowY="auto"
                    w="240px"
                    bg="white"
                    borderRight="1px solid"
                    borderColor="gray.200"
                >
                    <Box px={5} mb={6}>
                        <Text color="gray.900" fontWeight={600} fontSize="sm" mb={1}>{agentName ?? "RekruterAI"}</Text>
                        <Text fontSize="xs" color="gray.500">{t("modal.subtitle")}</Text>
                    </Box>

                    <Box as="nav" display="flex" flexDirection="column" gap={1} px={3}>
                        {STEPS.map((step) => {
                            const isActive = currentStep === step.id;
                            const isDone = currentStep > step.id;
                            const hasError = submitAttempted && stepHasErrors(step.id, formik.errors);
                            const hasWarning = !hasError && visitedSteps.has(step.id) && stepHasErrors(step.id, warningErrors);
                            const circle = stepCircleProps(hasError, hasWarning, isDone, isActive);
                            return (
                                <Box key={step.id}>
                                    <Box
                                        as="button"
                                        type="button"
                                        onClick={() => goToStep(step.id)}
                                        w="full"
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        px={3}
                                        py={2.5}
                                        borderRadius={12}
                                        textAlign="left"
                                        bg={isActive ? "purple.50" : "transparent"}
                                        border="1px solid"
                                        borderColor={stepAlertBorderColor(hasError, hasWarning) ?? (isActive ? "purple.200" : "transparent")}
                                    >
                                        <Box
                                            flexShrink={0}
                                            w={6}
                                            h={6}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="xs"
                                            fontWeight={700}
                                            bg={circle.bg}
                                            color={circle.color}
                                        >
                                            {circle.label ?? step.id}
                                        </Box>
                                        <Text fontSize="sm" fontWeight={500} color={stepLabelColor(isActive)}>
                                            {step.shortTitle}
                                        </Text>
                                    </Box>
                                    {isActive && (
                                        <Box ml={9} mt={1} mb={1} display="flex" flexDirection="column" gap={0.5}>
                                            {step.sections.map((sec) => (
                                                <Text key={sec} fontSize="xs" px={2} py={0.5} color="gray.500">
                                                    {sec}
                                                </Text>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box mt="auto" px={5} pt={4}>
                        {accountScoringEnabled && (
                            <ScoringToggle
                                checked={formik.values.scoringEnabled}
                                onChange={(v) => formik.setFieldValue("scoringEnabled", v)}
                            />
                        )}
                        {scoringActive && totalMaxScore > 0 && (
                            <Box
                                mb={4}
                                p={3}
                                borderRadius={10}
                                border="1px solid"
                                borderColor="gray.200"
                                bg="gray.50"
                            >
                                <Text fontSize="xs" color="gray.500" mb={0.5}>{t("modal.scoringSummary.title")}</Text>
                                <Text fontSize="lg" fontWeight={700} color="gray.900">
                                    {totalMaxScore} {t("modal.scoringSummary.points")}
                                </Text>
                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                    {t("modal.scoringSummary.breakdown", { scenarioCount: scenarioCriteriaCount, recruitmentCount: recruitmentCriteriaCount })}
                                </Text>
                            </Box>
                        )}
                        <Box display="flex" justifyContent="space-between" fontSize="xs" color="gray.500" mb={1.5}>
                            <Text>{t("modal.progress")}</Text>
                            <Text>{Math.round(((currentStep - 1) / totalSteps) * 100)}%</Text>
                        </Box>
                        <Box h={1.5} borderRadius="full" overflow="hidden" bg="gray.100">
                            <Box
                                h="full"
                                borderRadius="full"
                                bg="purple.500"
                                width={`${((currentStep - 1) / totalSteps) * 100}%`}
                                transition="width 0.3s"
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Main content */}
                <Box display="flex" flexDirection="column" flex={1} minW={0}>
                    {/* Mobile step bar */}
                    <Box
                        display={{ base: 'flex', md: 'none' }}
                        px={3}
                        py={3}
                        bg="white"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                    >
                        <HStack justify="space-between" w="full" gap={1} align="flex-start">
                            {STEPS.map((step) => {
                                const isActive = currentStep === step.id;
                                const isDone = currentStep > step.id;
                                const hasError = submitAttempted && stepHasErrors(step.id, formik.errors);
                                const hasWarning = !hasError && visitedSteps.has(step.id) && stepHasErrors(step.id, warningErrors);
                                const circle = stepCircleProps(hasError, hasWarning, isDone, isActive);
                                return (
                                    <Box
                                        key={step.id}
                                        as="button"
                                        type="button"
                                        onClick={() => goToStep(step.id)}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        gap={1}
                                        flex={1}
                                    >
                                        <Box
                                            flexShrink={0}
                                            w={8}
                                            h={8}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="xs"
                                            fontWeight={700}
                                            bg={circle.bg}
                                            color={circle.color}
                                            border="2px solid"
                                            borderColor={stepAlertBorderColor(hasError, hasWarning) ?? (isActive ? "purple.400" : "transparent")}
                                            transition="all 0.2s"
                                        >
                                            {circle.label ?? step.id}
                                        </Box>
                                        <Text
                                            fontSize="9px"
                                            color={stepLabelColor(isActive)}
                                            textAlign="center"
                                            lineHeight={1.2}
                                            maxW="56px"
                                            overflowWrap="break-word"
                                            wordBreak="normal"
                                        >
                                            {step.shortTitle}
                                        </Text>
                                    </Box>
                                );
                            })}
                        </HStack>
                    </Box>

                    {/* Header */}
                    <Box
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={{ base: 4, md: 6 }}
                        py={4}
                        bg="white"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        gap={2}
                    >
                        <Box minW={0}>
                            <Text color="gray.900" fontSize={{ base: 'sm', md: 'lg' }} fontWeight={700} truncate>
                                {activeStep.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {t("modal.stepCounter", { current: currentStep, total: totalSteps })}
                            </Text>
                            <Text display={{ base: 'none', md: 'block' }} fontSize="xs" color="gray.500" mt={1} maxW="480px">
                                {t(`modal.steps.${currentStep}.description`)}
                            </Text>
                        </Box>

                        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
                            {/* JSON buttons — desktop only */}
                            <Box display={{ base: 'none', md: 'flex' }} alignItems="center" gap={2}>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleSaveJson}
                                    px={3}
                                    py={2}
                                    borderRadius={8}
                                    fontSize="xs"
                                    bg="white"
                                    color="gray.700"
                                    border="1px solid"
                                    borderColor="gray.300"
                                >
                                    Zapisz jako JSON
                                </Box>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={() => setShowJsonModal(true)}
                                    px={3}
                                    py={2}
                                    borderRadius={8}
                                    fontSize="xs"
                                    bg="white"
                                    color="gray.700"
                                    border="1px solid"
                                    borderColor="gray.300"
                                >
                                    Wgraj JSON
                                </Box>
                                {saved && (
                                    <Text fontSize="xs" px={3} py={1.5} borderRadius={8} bg="green.100" color="green.700">
                                        {t("modal.saved")}
                                    </Text>
                                )}
                            </Box>

                            {/* Draft save — both */}
                            {renderDraftButton("xs")}

                            {/* Mobile error dot */}
                            {activeStepHasErrors && (
                                <Box
                                    display={{ base: 'flex', md: 'none' }}
                                    w={2}
                                    h={2}
                                    borderRadius="full"
                                    bg="red.500"
                                    flexShrink={0}
                                />
                            )}

                            <Box
                                as="button"
                                type="button"
                                onClick={onClose}
                                w={8}
                                h={8}
                                borderRadius={8}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="lg"
                                color="gray.500"
                                _hover={{ bg: "gray.100" }}
                            >
                                ×
                            </Box>
                        </Box>
                    </Box>

                    {/* Form content */}
                    <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
                        {activeStepHasErrors && (
                            <Box
                                role="alert"
                                mb={4}
                                px={4}
                                py={3}
                                borderRadius={10}
                                bg="red.50"
                                border="1px solid"
                                borderColor="red.200"
                            >
                                <Text fontSize="sm" color="red.700" fontWeight={600}>
                                    {t("modal.missingFieldsWarning")}
                                </Text>
                            </Box>
                        )}
                        <Box display={{ base: "block", md: "none" }}>
                            {accountScoringEnabled && (
                                <ScoringToggle
                                    checked={formik.values.scoringEnabled}
                                    onChange={(v) => formik.setFieldValue("scoringEnabled", v)}
                                />
                            )}
                        </Box>
                        <ScoringActiveContext.Provider value={scoringActive}>
                        <ScoringValidationContext.Provider value={showScoringErrors}>
                        {currentStep === 1 && <Step1Offer formik={formik} warningErrors={warningErrors} visited={visitedSteps.has(1)} forceOpen={stepNeedsForceOpen(1)} />}
                        {currentStep === 2 && <Step2Employment formik={formik} warningErrors={warningErrors} visited={visitedSteps.has(2)} forceOpen={stepNeedsForceOpen(2)} />}
                        {currentStep === 3 && <Step3Safety formik={formik} warningErrors={warningErrors} visited={visitedSteps.has(3)} forceOpen={stepNeedsForceOpen(3)} />}
                        {currentStep === 4 && <Step4Requirements formik={formik} warningErrors={warningErrors} visited={visitedSteps.has(4)} forceOpen={stepNeedsForceOpen(4)} />}
                        {currentStep === 5 && <Step5AIConfig formik={formik} warningErrors={warningErrors} visited={visitedSteps.has(5)} forceOpen={stepNeedsForceOpen(5)} scoringModel={scoringModel} onScoringModelLoaded={setScoringModel} />}
                        </ScoringValidationContext.Provider>
                        </ScoringActiveContext.Provider>
                    </Box>

                    {/* Footer */}
                    <Box
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={{ base: 4, md: 6 }}
                        py={4}
                        bg="white"
                        borderTop="1px solid"
                        borderColor="gray.200"
                    >
                        <Box
                            as="button"
                            type="button"
                            onClick={() => goToStep(Math.max(1, currentStep - 1))}
                            disabled={currentStep === 1 || formik.isSubmitting}
                            display="flex"
                            alignItems="center"
                            gap={2}
                            px={5}
                            py={2.5}
                            borderRadius={12}
                            fontSize="sm"
                            bg="white"
                            color="gray.600"
                            border="1px solid"
                            borderColor="gray.300"
                            opacity={currentStep === 1 || formik.isSubmitting ? 0.3 : 1}
                            cursor={currentStep === 1 || formik.isSubmitting ? "not-allowed" : "pointer"}
                            transition="background 0.15s, border-color 0.15s"
                            _hover={currentStep === 1 || formik.isSubmitting ? undefined : { bg: "gray.50", borderColor: "purple.400" }}
                        >
                            ← {t("modal.back")}
                        </Box>

                        {/* Desktop step dots */}
                        <Box display={{ base: 'none', md: 'flex' }} alignItems="center" gap={2}>
                            {STEPS.map((s) => {
                                const hasError = submitAttempted && stepHasErrors(s.id, formik.errors);
                                const hasWarning = !hasError && visitedSteps.has(s.id) && stepHasErrors(s.id, warningErrors);
                                return (
                                    <Box
                                        as="button"
                                        type="button"
                                        key={s.id}
                                        onClick={() => goToStep(s.id)}
                                        borderRadius="full"
                                        h={2}
                                        w={currentStep === s.id ? 6 : 2}
                                        bg={hasError ? "red.500" : hasWarning ? "orange.600" : currentStep === s.id ? "purple.500" : currentStep > s.id ? "purple.200" : "gray.200"}
                                        transition="all 0.2s"
                                    />
                                );
                            })}
                        </Box>

                        {/* Mobile: step counter text */}
                        <Text display={{ base: 'block', md: 'none' }} fontSize="xs" color="gray.500">
                            {currentStep} / {totalSteps}
                        </Text>

                        {currentStep < totalSteps ? (
                            <Box
                                as="button"
                                type="button"
                                onClick={() => goToStep(Math.min(totalSteps, currentStep + 1))}
                                disabled={formik.isSubmitting}
                                display="flex"
                                alignItems="center"
                                gap={2}
                                px={5}
                                py={2.5}
                                borderRadius={12}
                                fontSize="sm"
                                color="white"
                                bgGradient="to-r"
                                gradientFrom="#9D00FF"
                                gradientVia="#9000E9"
                                gradientTo="#8500D8"
                                opacity={formik.isSubmitting ? 0.5 : 1}
                                cursor={formik.isSubmitting ? "not-allowed" : "pointer"}
                                transition="filter 0.15s"
                                _hover={formik.isSubmitting ? undefined : { filter: "brightness(1.1)" }}
                            >
                                {t("modal.next")} →
                            </Box>
                        ) : (
                            <Box display="flex" gap={2}>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleSaveDraft}
                                    disabled={formik.isSubmitting}
                                    display={{ base: 'none', md: 'flex' }}
                                    alignItems="center"
                                    gap={2}
                                    px={5}
                                    py={2.5}
                                    borderRadius={12}
                                    fontSize="sm"
                                    bg="white"
                                    color="gray.600"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    opacity={formik.isSubmitting ? 0.5 : 1}
                                    cursor={formik.isSubmitting ? "not-allowed" : "pointer"}
                                    transition="background 0.15s, border-color 0.15s"
                                    _hover={formik.isSubmitting ? undefined : { bg: "gray.50", borderColor: "purple.400" }}
                                >
                                    {formik.isSubmitting && isDraft ? <Spinner size="sm" /> : t("modal.saveDraft")}
                                </Box>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handlePublish}
                                    disabled={formik.isSubmitting}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    px={6}
                                    py={2.5}
                                    borderRadius={12}
                                    fontSize="sm"
                                    color="white"
                                    bg="green.500"
                                    opacity={formik.isSubmitting ? 0.5 : 1}
                                    cursor={formik.isSubmitting ? "not-allowed" : "pointer"}
                                    transition="background 0.15s"
                                    _hover={formik.isSubmitting ? undefined : { bg: "green.600" }}
                                >
                                    {formik.isSubmitting && !isDraft ? <Spinner size="sm" /> : `✓ ${t("modal.createOffer")}`}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>

        {showJsonModal && (
            <Box
                position="fixed"
                inset={0}
                zIndex={100}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="rgba(0,0,0,0.7)"
                px={4}
            >
                <Box
                    bg="white"
                    borderRadius={16}
                    p={6}
                    w="full"
                    maxW="560px"
                    display="flex"
                    flexDirection="column"
                    gap={4}
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Text color="gray.900" fontWeight={700} fontSize="md">Wgraj JSON</Text>
                    <Box
                        as="textarea"
                        value={jsonInput}
                        onChange={(e) => { setJsonInput(e.target.value); setJsonError(""); }}
                        rows={12}
                        p={3}
                        borderRadius={8}
                        bg="gray.50"
                        color="gray.900"
                        fontSize="sm"
                        border="1px solid"
                        borderColor={jsonError ? "red.400" : "gray.300"}
                        resize="vertical"
                        fontFamily="mono"
                    />
                    {jsonError && <Text color="red.600" fontSize="xs">{jsonError}</Text>}
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box
                            as="label"
                            px={3}
                            py={2}
                            borderRadius={8}
                            fontSize="sm"
                            bg="white"
                            color="gray.600"
                            border="1px solid"
                            borderColor="gray.300"
                            cursor="pointer"
                        >
                            Wybierz plik
                            <Box as="input" type="file" accept=".json" display="none" onChange={handleFileChange} />
                        </Box>
                        <Box
                            as="button"
                            type="button"
                            onClick={handleLoadMockup}
                            px={3}
                            py={2}
                            borderRadius={8}
                            fontSize="sm"
                            bg="purple.50"
                            color="purple.700"
                            border="1px solid"
                            borderColor="purple.200"
                        >
                            Wgraj przykładowy mockup
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Box
                            as="button"
                            type="button"
                            onClick={() => { setShowJsonModal(false); setJsonInput(""); setJsonError(""); }}
                            px={4}
                            py={2}
                            borderRadius={8}
                            fontSize="sm"
                            bg="white"
                            color="gray.600"
                            border="1px solid"
                            borderColor="gray.300"
                        >
                            Anuluj
                        </Box>
                        <Box
                            as="button"
                            type="button"
                            onClick={handleLoadJson}
                            px={4}
                            py={2}
                            borderRadius={8}
                            fontSize="sm"
                            bg="green.600"
                            color="white"
                        >
                            Załaduj
                        </Box>
                    </Box>
                </Box>
            </Box>
        )}
        </>
    );
};

export default JobOfferFormModal;
